/**
 * Argus Live — reference pack manifest schema (AL-B5).
 *
 * Validates the on-disk JSON shape produced by `packs/argus-live-reference-pack.v1.json`.
 *
 * Forward-compat note:
 *   This schema is a v1 stop-gap that AL-C3 will subsume into the
 *   IMPORT-EXPORT-SPEC `BundleManifestSchema` discriminated union as a
 *   `dashboard` entity-kind under `bundleKind: 'argus_pack'`. The fields
 *   declared here are a strict subset of that future shape so a v1 pack
 *   reads cleanly through the AL-C3 importer.
 *
 * See:
 *   - docs/ARGUS-LIVE-SPEC.md §8.4 (export/import flow)
 *   - docs/cross-cutting/IMPORT-EXPORT-SPEC.md §2.2.1.3 (argus_pack manifest)
 *   - packs/argus-live-reference-pack.v1.json (the canonical example)
 *   - docs/argus-live/reference-pack.md (install + customization guide)
 *
 * Entity content shape mirrors `DashboardBindingSchema` + the publish
 * inputs (`codeHtml`, `codeJs`, `codeCss`, `dataBindings`,
 * `sandboxConfig`, `classificationTag`) from the AL-A2 routes — same
 * field names, same camelCase convention — so a pack entity can feed
 * `POST /argus-live/dashboards/:id/publish` with minimal mapping.
 */

import { z } from 'zod';
import {
  DashboardBindingManifestSchema,
  DashboardClassificationSchema,
  DashboardSandboxConfigSchema,
  DashboardSlugSchema,
} from './argus-live.schema.js';

// ─── Per-entity content shape ────────────────────────────────────────────

export const ArgusLivePackDashboardContentSchema = z.object({
  title: z.string().min(1).max(200),
  description: z.string().max(2000).nullable().optional().default(null),
  icon: z.string().max(80).nullable().optional().default(null),
  codeHtml: z.string().min(1),
  codeCss: z.string(),
  codeJs: z.string(),
  /**
   * Bindings array — matches the runtime DashboardBindingManifestSchema.
   * Empty array allowed (a static template with no live data).
   */
  dataBindings: DashboardBindingManifestSchema.default([]),
  /**
   * Sandbox knobs. Optional — runtime applies defaults when missing.
   */
  sandboxConfig: DashboardSandboxConfigSchema.partial().default({}),
  classificationTag: DashboardClassificationSchema,
});
export type ArgusLivePackDashboardContent = z.infer<
  typeof ArgusLivePackDashboardContentSchema
>;

export const ArgusLivePackDashboardEntitySchema = z.object({
  kind: z.literal('dashboard'),
  slug: DashboardSlugSchema,
  /**
   * Semver-ish rev tag the pack publisher assigns. v1 keeps it free-form
   * to match the IMPORT-EXPORT §8.4 spec example; AL-C3 will tighten
   * this to SemverSchema.
   */
  rev: z.string().min(1).max(40),
  content: ArgusLivePackDashboardContentSchema,
  /**
   * Tool slugs the dashboard's bindings reference. Used by the AL-C3
   * importer to fail the pack-apply with INSUFFICIENT_DEPENDENCIES when
   * the destination tenant is missing a required MCP tool.
   */
  dependencies: z
    .array(
      z.object({
        kind: z.literal('mcp_tool_slug'),
        slug: z.string().min(1).max(200),
      }),
    )
    .default([]),
});
export type ArgusLivePackDashboardEntity = z.infer<
  typeof ArgusLivePackDashboardEntitySchema
>;

// ─── Manifest-level shape ────────────────────────────────────────────────

export const ArgusLivePackPublisherSchema = z.object({
  kind: z.enum(['argus', 'certified-partner', 'community']),
  slug: z.string().min(1).max(128),
  email: z.string().email().optional(),
});

export const ArgusLivePackCompatibilitySchema = z.object({
  minPlatformVersion: z
    .string()
    .regex(
      /^\d+\.\d+\.\d+(?:-[0-9A-Za-z-.]+)?(?:\+[0-9A-Za-z-.]+)?$/,
      'minPlatformVersion must be semver',
    ),
  maxPlatformVersion: z
    .string()
    .regex(
      /^\d+\.\d+\.\d+(?:-[0-9A-Za-z-.]+)?(?:\+[0-9A-Za-z-.]+)?$/,
      'maxPlatformVersion must be semver',
    )
    .optional(),
});

export const ArgusLivePackLifecycleSchema = z.object({
  state: z.enum(['alpha', 'beta', 'stable', 'deprecated', 'retracted']),
  deprecationNotice: z.string().optional(),
  supersededBy: z.string().optional(),
});

export const ArgusLivePackLicenseSchema = z.object({
  identifier: z.string().min(1).max(128),
  url: z.string().url().optional(),
});

/**
 * bundleKind alias (AL-C3) — accept both:
 *
 *   - `argus-live-pack.v1`   (legacy AL-B5 reference-pack shape)
 *   - `argus_pack`           (IMPORT-EXPORT-SPEC §2.2.1.3 canonical subset)
 *
 * Forward + backward compat: AL-B5's reference pack continues to load
 * unchanged, while the new export endpoint emits the canonical
 * `argus_pack` kind. The remainder of the manifest fields are identical
 * across the two kinds (sub-set of the full IMPORT-EXPORT bundle), so a
 * single Zod object covers both with a union on `bundleKind`.
 */
export const ARGUS_PACK_BUNDLE_KINDS = [
  'argus-live-pack.v1',
  'argus_pack',
] as const;
export type ArgusPackBundleKind = (typeof ARGUS_PACK_BUNDLE_KINDS)[number];

export const ArgusPackBundleKindSchema = z.enum(ARGUS_PACK_BUNDLE_KINDS);

export const ArgusLivePackManifestSchema = z
  .object({
    $comment: z.string().optional(),
    bundleFormatVersion: z.literal(1),
    bundleKind: ArgusPackBundleKindSchema,
    id: z.string().uuid(),
    createdAt: z.string().datetime(),
    createdBy: z.string().uuid().nullable(),
    sourceTenantId: z.string().uuid().nullable(),
    manifestVersion: z.literal('argus.pack/v1'),
    packVersion: z
      .string()
      .regex(
        /^\d+\.\d+\.\d+(?:-[0-9A-Za-z-.]+)?(?:\+[0-9A-Za-z-.]+)?$/,
        'packVersion must be semver',
      ),
    publisher: ArgusLivePackPublisherSchema,
    compatibility: ArgusLivePackCompatibilitySchema,
    lifecycle: ArgusLivePackLifecycleSchema,
    license: ArgusLivePackLicenseSchema,
    documentationUrl: z.string().url().optional(),
    contents: z.object({
      entityKinds: z.array(z.string()).min(1),
      entityCount: z.number().int().min(1),
    }),
    classificationCeiling: DashboardClassificationSchema,
    totalBundleBytes: z.number().int().min(1),
    entryCount: z.number().int().min(1),
    contentMerkleRoot: z
      .string()
      .regex(/^sha256:[a-f0-9]{64}$/, 'contentMerkleRoot must be sha256:<hex>'),
    declaredByteRanges: z.array(z.unknown()).default([]),
    entities: z.array(ArgusLivePackDashboardEntitySchema).min(1),
    signatureBlock: z.object({
      alg: z.enum(['RS256', 'Ed25519']),
      keyId: z.string().min(1).max(200),
      sig: z.string().min(1).max(4096),
    }),
  })
  .strict()
  .superRefine((manifest, ctx) => {
    if (manifest.contents.entityCount !== manifest.entities.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `contents.entityCount=${manifest.contents.entityCount} does not match entities.length=${manifest.entities.length}`,
        path: ['contents', 'entityCount'],
      });
    }
    const slugs = new Set<string>();
    for (let i = 0; i < manifest.entities.length; i++) {
      const slug = manifest.entities[i].slug;
      if (slugs.has(slug)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Duplicate dashboard slug ${slug}`,
          path: ['entities', i, 'slug'],
        });
      }
      slugs.add(slug);
    }
  });
export type ArgusLivePackManifest = z.infer<typeof ArgusLivePackManifestSchema>;

/**
 * Parse + validate. Throws ZodError on invalid input.
 */
export function parseArgusLivePack(raw: unknown): ArgusLivePackManifest {
  return ArgusLivePackManifestSchema.parse(raw);
}
